import React, { useEffect, useRef, useState } from 'react';

interface DeleteConfirmDialogProps {
  /** e.g. "Delete Dataset" — used for the heading and the confirm button. */
  title: string;
  /** Explanation of what is about to happen, shown above the id. */
  description: string;
  /** Monospace identifier(s) of the thing being deleted. */
  itemId: string;
  /** Consequence line shown below the id, e.g. "Its samples return to the unassigned pool." */
  consequence?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Confirmation modal requiring the user to type DELETE before a hard-delete. */
export const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  title,
  description,
  itemId,
  consequence,
  onConfirm,
  onCancel,
}) => {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 24,
          width: 420,
          maxWidth: '90vw',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: 'var(--danger)' }}>
          {title}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
          {description}
        </div>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--text-primary)',
            marginBottom: 12,
            wordBreak: 'break-all',
          }}
        >
          {itemId}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
          {consequence ? `${consequence} ` : ''}This action cannot be undone.
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
          Type <strong style={{ color: 'var(--text-primary)' }}>DELETE</strong> to confirm:
        </div>
        <input
          ref={inputRef}
          className="eval-form-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && input === 'DELETE') onConfirm();
          }}
          placeholder="DELETE"
          style={{ marginBottom: 16, fontFamily: 'var(--font-mono)' }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="eval-btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button className="eval-btn-danger" disabled={input !== 'DELETE'} onClick={onConfirm}>
            {title}
          </button>
        </div>
      </div>
    </div>
  );
};
